import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema } from '../../shared/validation/schemas';
import type { LoginFormData } from '../../shared/validation/schemas';
import { useAuth } from '../../modules/auth/hooks/useAuth';
import { UserCacheService } from '../../modules/auth/services/user-cache.service';
import { RememberedUser } from '../../modules/auth/ui/RememberedUser';
import { AuthProviders } from '../../modules/auth/ui/AuthProviders';
import Button from '../../components/base/Button';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [showRememberedUser, setShowRememberedUser] = useState(true);

  // Form para login
  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  });

  const handleSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      await login(data.email, data.password);
      
      // Guardar en cache solo si el usuario marcó "Recordarme"
      if (rememberMe) {
        UserCacheService.saveUser(data.email);
      } else {
        // Si no quiere ser recordado, limpiar cualquier cache existente
        UserCacheService.clearUser();
      }
      
      navigate('/dashboard');
    } catch (err: unknown) {
      setError((err as Error).message || 'Error al iniciar sesión');
    } finally {
      setIsLoading(false);
    }
  };



  const handleContinueWithRemembered = (email: string) => {
    // Pre-llenar el formulario con el email recordado
    form.setValue('email', email);
    setShowRememberedUser(false);
    setRememberMe(true); // Marcar recordarme como verdadero
    // Focus en el campo de contraseña
    setTimeout(() => {
      const passwordField = document.getElementById('password');
      passwordField?.focus();
    }, 100);
  };

  const handleClearRemembered = () => {
    setShowRememberedUser(false);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://readdy.ai/api/search-image?query=Ultra%20futuristic%20financial%20dashboard%20interface%20with%20holographic%20displays%2C%20neon%20blue%20and%20green%20glowing%20elements%2C%20advanced%20AI%20analytics%20screens%2C%20floating%20data%20visualizations%2C%20cyberpunk%20style%20financial%20technology%2C%20dark%20background%20with%20bright%20accent%20lights%2C%20modern%20glass%20surfaces%2C%20high-tech%20trading%20floor%20atmosphere%2C%20digital%20financial%20charts%20and%20graphs%2C%20sleek%20minimalist%20design%20with%20glowing%20borders&width=1920&height=1080&seq=login-futuristic&orientation=landscape')`
      }}
    >
      {/* Efectos de partículas animadas */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-emerald-400 rounded-full animate-pulse opacity-60"></div>
        <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-blue-400 rounded-full animate-ping opacity-40"></div>
        <div className="absolute bottom-1/4 left-1/3 w-3 h-3 bg-emerald-300 rounded-full animate-bounce opacity-30"></div>
        <div className="absolute top-2/3 right-1/4 w-2 h-2 bg-blue-300 rounded-full animate-pulse opacity-50"></div>
      </div>

      <div className="w-full max-w-md mx-auto px-6 relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: '"Pacifico", serif' }}>
            Invirtiendo
          </h1>
          <p className="text-emerald-300 text-lg">Tu futuro financiero comienza aquí</p>
        </div>

        {/* Formulario principal */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-2xl border border-white/20">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              Bienvenido de vuelta
            </h2>
            <p className="text-gray-300">
              Accede a tu dashboard financiero
            </p>
          </div>

          {/* Usuario recordado */}
          {showRememberedUser && UserCacheService.hasRememberedUser() && (
            <div className="mb-6">
              <RememberedUser
                onContinue={handleContinueWithRemembered}
                onClear={handleClearRemembered}
              />
            </div>
          )}

          {/* Mostrar errores */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-400/50 text-red-300 rounded-lg backdrop-blur-sm">
              {error}
            </div>
          )}

          {/* Formulario de Login */}
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-mail-line text-gray-400"></i>
                </div>
                <input
                  {...form.register('email')}
                  type="email"
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent backdrop-blur-sm"
                  placeholder="tu@email.com"
                />
              </div>
              {form.formState.errors.email && (
                <p className="mt-1 text-sm text-red-400">{form.formState.errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-lock-line text-gray-400"></i>
                </div>
                <input
                  {...form.register('password')}
                  type={showPassword ? 'text' : 'password'}
                  className="w-full pl-10 pr-12 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent backdrop-blur-sm"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-gray-400 hover:text-white transition-colors`}></i>
                </button>
              </div>
              {form.formState.errors.password && (
                <p className="mt-1 text-sm text-red-400">{form.formState.errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-emerald-600 bg-white/10 border-white/20 rounded focus:ring-emerald-500 focus:ring-2"
                />
                <span className="ml-2 text-sm text-gray-300">Recordarme</span>
              </label>
              <button
                type="button"
                className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors cursor-pointer"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              size="lg"
              className="w-full bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 text-white font-semibold py-4 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Iniciando sesión...
                </div>
              ) : (
                <>
                  <i className="ri-login-box-line mr-2"></i>
                  Iniciar Sesión
                </>
              )}
            </Button>
          </form>

          {/* Autenticación Social */}
          <AuthProviders />

          {/* Link a registro */}
          <div className="mt-6 text-center">
            <p className="text-gray-300">
              ¿No tienes cuenta?
              <button
                type="button"
                onClick={() => navigate('/register')}
                disabled={isLoading}
                className="ml-1 text-emerald-400 hover:text-emerald-300 font-medium transition-colors cursor-pointer disabled:opacity-50"
              >
                Regístrate aquí
              </button>
            </p>
          </div>
        </div>

        {/* Características destacadas */}
        <div className="mt-8 grid grid-cols-3 gap-4 text-center">
          <div className="text-white/80">
            <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
              <i className="ri-shield-check-line text-emerald-400 text-xl"></i>
            </div>
            <p className="text-xs">100% Seguro</p>
          </div>
          <div className="text-white/80">
            <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
              <i className="ri-ai-generate text-blue-400 text-xl"></i>
            </div>
            <p className="text-xs">IA Avanzada</p>
          </div>
          <div className="text-white/80">
            <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
              <i className="ri-rocket-line text-emerald-400 text-xl"></i>
            </div>
            <p className="text-xs">Fácil de usar</p>
          </div>
        </div>

        {/* Botón de volver */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            disabled={isLoading}
            className="text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center mx-auto disabled:opacity-50"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Volver al inicio
          </button>
        </div>
      </div>
    </div>
  );
}